import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text, Button, Icon, StatCell, StatsWrap, Badge, Eyebrow, DrillChevron, EmptyState, Skeleton } from '@/nova/components';
import { useClipboard } from '@/nova/components/CopyButton';
import { FtuxTarget } from '@/nova/features/ftux/FtuxTarget';
import type { FtuxTargetId } from '@/nova/features/ftux/targets';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { MOBILE } from '@/nova/theme/tokens';
import { expiresInShort, type AffiliateCardVM } from '../../mapping';
import type { OrgInvite } from '../../types';
import { Notice } from '../Notice/Notice';
import { ROSTER_PLAN_FILTERS, PLAN_TYPE_LABEL, type PlanFilter } from '../../constants';
import { HeaderLeft, HeaderTitle, Count, Panel, AffList, AffCard, AffTop, AffName, BoxedStatGrid, Stack } from '../../affiliates.styles';
// The filter chips, search control and list-card animation are the acquisition Campaigns browser's
// shared vocabulary (importing them cross-page is the established precedent: operations + wallet do it).
import {
  BrowserHead,
  FilterLbl,
  CategoryChips,
  CategoryPill,
  SearchSlot,
  SearchControl,
  SearchIconBtn,
  SearchDivider,
  SearchDismiss,
  SearchRow,
  SearchPill,
  SearchField,
  SearchClear,
  SearchResults,
} from '@/nova/pages/acquisition/components/Campaigns/CampaignBrowser.styles';
import { Cell, LoadMoreButton } from '@/nova/pages/acquisition/components/Campaigns/Campaigns.styles';
// Reuse the players listing's deterministic avatar mnemonic (pure helpers) for the roster avatar.
import { avatarColor, avatarInitial } from '@/nova/pages/players/format';
import { usePagination } from '@/nova/hooks/usePagination';
import {
  RightGroup,
  SkeletonAffCard,
  RosterReveal,
  HeaderActions,
  RosterHeader,
  InviteButton,
  HeaderPendingToggle,
  MobilePendingToggle,
  NameGroup,
  Avatar,
  AvatarInitial,
  SectionBlock,
  SectionEyebrow,
  PendingList,
  PendingCard,
  PendingMeta,
  PendingEmail,
  PendingActions,
  ExpiryText,
  PendingRegion,
  PendingReveal,
  PendingRevealInner,
  PendingErrorHolder,
} from './AffiliatesTab.styles';

/** Initial-in-a-circle avatar for a roster card, coloured deterministically from the seed (the
 *  displayed name), matching the players listing. Decorative; the name sits beside it. */
const RosterAvatar = ({ seed }: { seed: string }): React.ReactElement => {
  const { bg, fg } = avatarColor(seed);
  return (
    <Avatar $bg={bg} aria-hidden="true">
      <AvatarInitial text={avatarInitial(seed)} tag="span" $fg={fg} />
    </Avatar>
  );
};

/**
 * A pending email invitation, kept to a single compact row (only about as tall as the avatar) so a
 * few pending invites never bury the roster: the invitee's email with its time-to-expiry stacked
 * underneath on the left, the copy-link + revoke actions on the right. Not clickable (there is no
 * affiliate to open until they accept). The block's "Pending invitations" eyebrow carries the
 * status, so the row itself needs no per-row status badge.
 */
const PendingInviteRow = ({
  invite,
  onRevoke,
}: {
  invite: OrgInvite;
  onRevoke: (id: string) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const { copied, copy } = useClipboard();
  const expiry = expiresInShort(invite.expires_at, Date.now());
  return (
    <PendingCard>
      <NameGroup>
        <RosterAvatar seed={invite.email ?? invite.id} />
        <PendingMeta>
          <PendingEmail text={invite.email ?? t('Anyone with the link')} tag="span" />
          {expiry ? (
            expiry.expired ? (
              <ExpiryText text="Expired" tag="span" />
            ) : (
              <ExpiryText text="Expires in {{time}}" placeholders={{ time: expiry.text }} tag="span" />
            )
          ) : null}
        </PendingMeta>
      </NameGroup>
      <PendingActions>
        <Button label={copied ? t('Copied') : t('Copy link')} variant="text" icon="copy" onClick={() => void copy(invite.link)} />
        <Button label={t('Revoke')} variant="text" icon="trash" onClick={() => onRevoke(invite.id)} />
      </PendingActions>
    </PendingCard>
  );
};

// Pending invitations shown per page; "Load more" reveals another page. Keeps the pending block short
// so it never buries the affiliates roster below it (mirrors the acquisition Campaigns "Load more").
const PENDING_PAGE_SIZE = 2;

// Remember the owner's show/hide choice for the pending-invitations block across visits (a plain UI
// preference, not brand data, so one global key). Guarded: localStorage can throw (private mode /
// blocked storage), in which case we fall back to shown and simply do not persist.
const PENDING_VISIBLE_KEY = 'ows-affiliates-pending-invites-visible';

const readPendingVisible = (): boolean => {
  try {
    const raw = window.localStorage.getItem(PENDING_VISIBLE_KEY);
    // Never toggled -> shown; only an explicit '0' hides it.
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
};

const writePendingVisible = (visible: boolean): void => {
  try {
    window.localStorage.setItem(PENDING_VISIBLE_KEY, visible ? '1' : '0');
  } catch {
    // Storage unavailable: the choice just will not persist.
  }
};

/*
 * Where the FTUX's affiliates piece lights the tab whole (its header, then the roster's panel), Invite affiliate marked
 * inside it (inert outside an FTUX step). The tab's blocks sit in a Stack of their own, the page's column and gap, so
 * they lay out as they did straight in the page's.
 */
const AREA_TARGET: FtuxTargetId = 'aff-area';

// How many placeholder cards to show while the roster loads.
const SKELETON_CARD_COUNT = 10;
// Name-line widths cycled per card so the ten placeholders don't read as a rigid grid.
const NAME_WIDTHS = [168, 140, 190, 150];
// The four figures every affiliate card shows. Labels are static chrome (kept visible);
// only the values load in, mirroring a real card's own loading state.
const SKELETON_STAT_KEYS = ['Regs', 'FTDs', 'Reg → FTD', 'Avg Dep'];

/**
 * A loading placeholder shaped exactly like a real affiliate card (same wrappers,
 * spacing and 4-stat grid) so the list never jumps on reveal. Decorative only
 * (`aria-hidden`); the `RosterReveal` wrapper cross-fades a grid of these out as the
 * real roster fades in.
 */
const SkeletonCard = ({ index }: { index: number }): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <SkeletonAffCard aria-hidden="true">
      <AffTop>
        <Skeleton width={NAME_WIDTHS[index % NAME_WIDTHS.length]} height={13} radius={5} />
        <RightGroup>
          <Skeleton width={44} height={13} radius={5} />
          <Skeleton circle width={22} height={22} />
        </RightGroup>
      </AffTop>
      <Skeleton width={64} height={11} radius={5} />
      <StatsWrap>
        <BoxedStatGrid $columns={4}>
          {SKELETON_STAT_KEYS.map((key) => (
            <StatCell key={key} label={t(key)} value="" loading />
          ))}
        </BoxedStatGrid>
      </StatsWrap>
    </SkeletonAffCard>
  );
};

export type AffiliatesTabProps = {
  roster: AffiliateCardVM[];
  loading: boolean;
  /** Filter by the affiliate's ASSIGNED commission plan type (URL-driven; 'all' = neutral). */
  filter: PlanFilter;
  onFilterSelect: (value: PlanFilter) => void;
  /** Email-bound pending invitations, shown above the roster (they need the owner's attention). */
  pendingInvites: OrgInvite[];
  /** A settled failure of the invites read, so a failed load surfaces instead of silently hiding invites. */
  invitesError?: string;
  /** Re-run the invites fetch (wired to the resource's reload). */
  onRetryInvites?: () => void;
  /** Revoke a pending invite by id (so it can no longer be redeemed). */
  onRevokeInvite: (id: string) => void;
  onInvite: () => void;
  onOpen: (id: string) => void;
};

export const AffiliatesTab = ({
  roster,
  loading,
  filter,
  onFilterSelect,
  pendingInvites,
  invitesError,
  onRetryInvites,
  onRevokeInvite,
  onInvite,
  onOpen,
}: AffiliatesTabProps): React.ReactElement => {
  const { t } = useTranslation();

  // Literal-keyed map so every stat / plan / chip label is harvested by the i18n extractor.
  const labels: Record<string, string> = {
    All: t('All'),
    CPA: t('CPA'),
    'Rev Share': t('Rev Share'),
    Hybrid: t('Hybrid'),
    Fixed: t('Fixed'),
    'No plan': t('No plan'),
    Affiliate: t('Affiliate'),
    Regs: t('Regs'),
    FTDs: t('FTDs'),
    'Reg → FTD': t('Reg → FTD'),
    'Avg Dep': t('Avg Dep'),
    'Cost / FTD': t('Cost / FTD'),
    'Rev / FTD': t('Rev / FTD'),
    ROI: t('ROI'),
    'Total Cost': t('Total Cost'),
  };
  const label = (value: string): string => labels[value] ?? value;

  // Search is local (like the Campaigns browser): a toggle that only shows/hides the field, and the
  // term that actually filters. The term keeps filtering even while the field is collapsed.
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  const toggleSearch = React.useCallback(() => setSearchOpen((open) => !open), []);
  const clearSearch = React.useCallback(() => {
    setQuery('');
    setSearchOpen(false);
  }, []);

  // Focus the field when it opens so the owner can type immediately (external system: the DOM node).
  React.useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  // Cap the pending-invite list at PENDING_PAGE_SIZE, revealing another page on "Load more", so a long
  // list of pending invitations never pushes the affiliates roster off-screen. Uses the shared
  // usePagination hook (as the channel-detail list does), so this call site swaps to a paged endpoint
  // the same way if one ever lands.
  const {
    visible: shownPending,
    hasMore: hasMorePending,
    loadMore: loadMorePending,
  } = usePagination(pendingInvites, PENDING_PAGE_SIZE);

  // The pending-invitations block collapses on demand so the owner can push it aside and see the
  // roster. The last choice is restored from localStorage (shown by default, since a pending invite
  // is the attention item); the header toggle only appears when there is a block to hide.
  const [showPending, setShowPending] = React.useState(readPendingVisible);
  const togglePending = React.useCallback(() => setShowPending((open) => !open), []);
  const hasPending = !invitesError && pendingInvites.length > 0;

  // Persist the choice so the next visit reopens the block the way it was left. localStorage is an
  // external store we sync to; the mount write is a harmless re-save of the value just read.
  React.useEffect(() => {
    writePendingVisible(showPending);
  }, [showPending]);

  // Filter by the assigned plan type first (URL-driven), then narrow by the search term (name/email).
  const byPlan =
    filter === 'all'
      ? roster
      : filter === 'none'
        ? roster.filter((card) => !card.planType)
        : roster.filter((card) => card.planType === filter);
  const q = query.trim().toLowerCase();
  const visible = q
    ? byPlan.filter((card) => card.name.toLowerCase().includes(q) || card.email.toLowerCase().includes(q))
    : byPlan;

  // Placeholder cards show only on the initial (empty) load; a background refresh of an
  // already-populated list refreshes each value in place (via StatCell's own loading)
  // instead of blanking the whole roster.
  const showSkeletons = loading && roster.length === 0;
  // Invite affiliate spans its row on a phone (InviteButton), so its FTUX mark does too; elsewhere it hugs the button.
  const inviteSpansRow = useMediaQuery(MOBILE);

  return (
    <Stack data-ftux={AREA_TARGET}>
      <PendingRegion>
        <RosterHeader>
          <HeaderLeft>
            <HeaderTitle text="Your Affiliates" tag="span" />
            <Count>{loading ? <Skeleton width="2ch" height="0.8em" radius={5} /> : roster.length}</Count>
          </HeaderLeft>
          <HeaderActions>
            {hasPending ? (
              <HeaderPendingToggle
                label={showPending ? t('Hide invitations') : t('Show invitations')}
                variant="text"
                icon={showPending ? 'chevron-up' : 'chevron-down'}
                onClick={togglePending}
              />
            ) : null}
            <FtuxTarget id="aff-invite" fill={inviteSpansRow}>
              <InviteButton label={t('Invite affiliate')} variant="primary" icon="mail" onClick={onInvite} />
            </FtuxTarget>
          </HeaderActions>
        </RosterHeader>

        {invitesError ? (
          <PendingErrorHolder>
            <SectionBlock>
              <SectionEyebrow text="Pending invitations" tag="span" />
              <Panel>
                <Notice
                  icon="warning"
                  title={t('Could not load invite links')}
                  body={invitesError}
                  actionLabel={onRetryInvites ? t('Retry') : undefined}
                  onAction={onRetryInvites}
                />
              </Panel>
            </SectionBlock>
          </PendingErrorHolder>
        ) : pendingInvites.length > 0 ? (
          // Show/hide collapses the whole block (height + its own top spacing) with an organic
          // grid-row reveal, so a hidden block reads exactly like having no pending invites.
          <PendingReveal $open={showPending}>
            <PendingRevealInner $open={showPending}>
              <SectionBlock>
                <SectionEyebrow text="Pending invitations" tag="span" />
                <Panel>
                  <PendingList>
                    {shownPending.map((invite) => (
                      <PendingInviteRow key={invite.id} invite={invite} onRevoke={onRevokeInvite} />
                    ))}
                    {hasMorePending ? (
                      <LoadMoreButton variant="text" icon="plus-circle" label={t('Load more')} onClick={loadMorePending} />
                    ) : null}
                  </PendingList>
                </Panel>
              </SectionBlock>
            </PendingRevealInner>
          </PendingReveal>
        ) : null}
        {hasPending ? (
          <MobilePendingToggle
            label={showPending ? t('Hide invitations') : t('Show invitations')}
            variant="text"
            icon={showPending ? 'chevron-up' : 'chevron-down'}
            onClick={togglePending}
          />
        ) : null}
      </PendingRegion>

      <SectionBlock>
        <SectionEyebrow text="Affiliates" tag="span" />
        <Panel>
        <BrowserHead>
          <FilterLbl text="Plan" tag="span" />
          <CategoryChips>
            {ROSTER_PLAN_FILTERS.map((option) => (
              <CategoryPill
                key={option.value}
                type="button"
                $on={filter === option.value}
                aria-pressed={filter === option.value}
                // Clicking the active plan chip returns to "All" (matches the Campaigns browser).
                onClick={() => onFilterSelect(filter === option.value ? 'all' : option.value)}
              >
                <Text text={label(option.label)} tag="span" />
              </CategoryPill>
            ))}
          </CategoryChips>
          <SearchSlot>
            <SearchControl $on={Boolean(q)}>
              <SearchIconBtn
                type="button"
                aria-label={searchOpen ? t('Hide search') : t('Search affiliates')}
                aria-expanded={searchOpen}
                onClick={toggleSearch}
              >
                <Icon name="search" size={16} />
              </SearchIconBtn>
              {!searchOpen && q ? (
                <>
                  <SearchDivider aria-hidden="true" />
                  <SearchDismiss type="button" aria-label={t('Clear search')} onClick={clearSearch}>
                    <Icon name="close" size={16} />
                  </SearchDismiss>
                </>
              ) : null}
            </SearchControl>
          </SearchSlot>
        </BrowserHead>

        {searchOpen ? (
          <SearchRow>
            <SearchPill>
              <Icon name="search" size={18} />
              <SearchField
                ref={searchRef}
                type="text"
                aria-label={t('Search affiliates')}
                placeholder={t('Search affiliates…')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') clearSearch();
                }}
              />
              {query ? (
                <SearchClear
                  type="button"
                  aria-label={t('Clear search text')}
                  onClick={() => {
                    setQuery('');
                    searchRef.current?.focus();
                  }}
                >
                  <Icon name="close" size={14} />
                </SearchClear>
              ) : null}
            </SearchPill>
            {q ? <SearchResults text="{{count}} results" placeholders={{ count: visible.length }} tag="span" /> : null}
          </SearchRow>
        ) : null}

        <RosterReveal
          loading={showSkeletons}
          placeholder={
            <AffList aria-hidden="true">
              {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
                <SkeletonCard key={i} index={i} />
              ))}
            </AffList>
          }
        >
          {visible.length === 0 ? (
            <EmptyState
              text={
                roster.length === 0
                  ? t('No affiliates yet')
                  : q
                    ? t('No affiliates match your search')
                    : t('No affiliates match this filter')
              }
            />
          ) : (
            // Re-key by the active plan filter so the whole list re-runs the card-enter animation on a
            // filter change (like the Campaigns browser). Search narrowing keeps the key, so typing
            // only animates newly-matching rows in, never re-shuffles the survivors on each keystroke.
            <AffList key={filter}>
              {visible.map((card) => (
                <Cell key={card.id}>
                  <AffCard
                    role="button"
                    tabIndex={0}
                    aria-label={t('Open {{name}}', { name: card.name })}
                    onClick={() => onOpen(card.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpen(card.id);
                      }
                    }}
                  >
                    <AffTop>
                      <NameGroup>
                        <RosterAvatar seed={card.name} />
                        <AffName text={card.name} tag="span" />
                      </NameGroup>
                      <RightGroup>
                        <Badge label={card.active ? t('Active') : t('Paused')} tone={card.active ? 'success' : 'neutral'} />
                        <DrillChevron />
                      </RightGroup>
                    </AffTop>
                    <Eyebrow
                      text={card.planName ?? t('No plan')}
                      detail={card.planType ? label(PLAN_TYPE_LABEL[card.planType]) : undefined}
                    />
                    <StatsWrap>
                      <BoxedStatGrid $columns={4}>
                        {card.stats.map((stat) => (
                          <StatCell key={stat.id} label={label(stat.label)} value={stat.value} tone={stat.tone} loading={loading} />
                        ))}
                      </BoxedStatGrid>
                    </StatsWrap>
                  </AffCard>
                </Cell>
              ))}
            </AffList>
          )}
        </RosterReveal>
      </Panel>
      </SectionBlock>
    </Stack>
  );
};

export default AffiliatesTab;
